import { stringify, parse } from 'qs';
import {
  formatDistanceStrict,
  format,
  differenceInCalendarDays,
  fromUnixTime,
  subDays,
  getUnixTime,
  getTime,
  set,
  differenceInDays,
} from 'date-fns';
import { Map, List, OrderedSet } from 'immutable';
import { constants as rootCauseConstants } from '@saucelabs/sl-module-root-cause';

import { FILTERS_MAP, FILTER_TYPES } from './ducks/filters/constants';
import * as urlQueryString from './urlQueryString';
import * as constants from './constants';
import {
  SORT_DIRECTION,
  TEST_START_DATE_FORMAT,
  TEST_START_TIME_MINUTE_FORMAT,
  TIME_AND_DATE_FORMAT,
  DATE_ONLY_FORMAT,
  CHART_BREAKPOINT_DAYS,
} from './constants';

const MAX_DAYS = 31;
const { OWNER_FILTER_SCOPES } = rootCauseConstants;
const FILTERS_STORE_SUFFIX = '__filters';

export const getStartEndDate = (dateFilter, base = new Date(), priorData = false) => {
  const type = dateFilter.get('type');
  const value = dateFilter.get('value');
  const startDate = type === 'absolute' ? new Date(value.startDate.getTime()) : new Date(base - (value * 1000));
  const endDate = type === 'absolute' ? new Date(value.endDate.getTime()) : new Date(base);
  const timeDiff = endDate - startDate;
  if (priorData) {
    return {
      startDate: new Date(startDate.getTime() - timeDiff),
      endDate: new Date(endDate.getTime() - timeDiff),
    };
  }

  return {
    startDate,
    endDate,
  };
};

/**
 * Checks whether organization scope should be enabled for the given user
 * @param {User} user
 * @returns boolean
 */
export const isOrganizationScopeEnabled = (user) => (
  user.isOrgAdmin() || user.isJobTeamSharingEnabled());

export const formatDuration = (selectedDateFilter, includeStartDay = false) => {
  const { startDate, endDate } = getStartEndDate(selectedDateFilter);
  const startDateModified = includeStartDay ? subDays(startDate, 1) : startDate;
  return formatDistanceStrict(startDateModified, endDate);
};

export const getFiltersFromQueryParams = (allQueryParams, defaultFilters = {}) => (
  Object.keys(allQueryParams)
    .reduce((ac, key) => ({
      ...ac,
      ...(FILTER_TYPES.includes(key) && { [key]: allQueryParams[key] }),
    }), defaultFilters)
);

export const getDateFilterFromQueryParams = (allQueryParams) => {
  const dateFilter = JSON.parse(allQueryParams.dateFilter);

  if (dateFilter.type === 'absolute') {
    const { value: { startDate, endDate } } = dateFilter;

    if (!startDate || !endDate) {
      return Map({
        ...dateFilter,
        value: {
          startDate: new Date(),
          endDate: new Date(),
        },
      });
    }

    return Map({
      ...dateFilter,
      value: {
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      },
    });
  }

  return Map(dateFilter);
};

export const selectedDateTransformer = (location, payload) => (
  urlQueryString.set(location, { dateFilter: JSON.stringify(payload) })
);

export const removeFilterTransformer = (location, payload) => (
  urlQueryString.remove(location, payload.name, (
    payload.name === constants.OWNER_FILTER_KEYS.OWNERS ? payload.value.name : payload.value
  ))
);

export const selectFilterTransformer = (location, payload) => (
  urlQueryString.set(location, {
    [payload.name]: payload.name === constants.OWNER_FILTER_KEYS.OWNERS ?
      payload.items.map(({ name }) => name).toJSON() :
      payload.items.toJSON(),
  })
);

export const clearSelectedFilterTransformer = (location) => {
  const allQueryParams = urlQueryString.getAll(location);
  return Object.keys(allQueryParams)
    .reduce((ac, param) => ({
      ...ac,
      ...(!FILTERS_MAP.get(param) && { [param]: allQueryParams[param] }),
    }), {});
};

export const calculatePercentage = (partialValue, totalValue) => (
  totalValue ? (100 * (partialValue || 0)) / totalValue : 0
);

export const formatLabel = (item) => (item || 0);

export const formatComparisonLabel = (item, date) => {
  const pastData = formatLabel(item.get('previousValue'));
  const currentData = formatLabel(item.get('value'));
  if (typeof currentData === 'string') return null;

  // right now we only store ~60 days of test data.  If the selected time range is greater
  // than 30 days we will not have accurate data to compare.
  if (date.get('value') > constants.MAX_DATE_RANGE) {
    return '--';
  }

  if (currentData === pastData) {
    return `vs. ${pastData} (0%)`;
  }

  //  if the prior number of tests ran is equal to 0 it's not possible to
  //  accurately calculate the percent
  if (pastData === 0) {
    return `vs. ${pastData} (--%)`;
  }

  const percentage = (((currentData - pastData) / pastData) * 100).toFixed(1);
  return `vs. ${pastData} (${percentage}%)`;
};

// method to get date interval for api call (minute / hour / day)
export const getInterval = (timeSpan) => {
  if (timeSpan > 60 * 60 * 24 * 3) {
    return '1d';
  }

  if (timeSpan <= 60 * 60) {
    return '1m';
  }

  return '1h';
};

/**
 * Formats the timestamp provided to a tooltip.
 *
 * @param {string} timestamp ISO string timestamp returned from the API.
 * @param {Map} selectedDateFilter The selected date filter currently applied to the UI. Can also be
 * an empty Immutable Map object.
 */
export const formatTooltipTimeValue = (timestamp, selectedDateFilter) => {
  const dateType = selectedDateFilter.get('type') ?? 'relative';
  const dateValue = selectedDateFilter.get('value') ?? 0;
  const interval = dateType === 'relative' ? dateValue : (
    dateValue.endDate - dateValue.startDate
  ) / 1000;
  const timeInterval = getInterval(interval);
  const timeFormat = timeInterval === '1d' ? TEST_START_DATE_FORMAT : TEST_START_TIME_MINUTE_FORMAT;

  return format(new Date(timestamp), timeFormat);
};

export const prepareTrendsTestsData = ({ buckets, start, end }) => {
  let totalTests = 0;
  let totalPassedTests = 0;
  const testsCountData = [];
  const statusCountData = [];
  const startDate = start * 1000;
  const endDate = end * 1000;

  if (!buckets.length) {
    return {
      totalTests,
      totalPassedTests,
      testsCountData,
      statusCountData,
    };
  }

  const prefixMissingTime = buckets[0].timestamp - startDate;
  const prefixMissingDays = prefixMissingTime ?
    Math.ceil(prefixMissingTime / constants.ONE_DAY) :
    0;
  const suffixMissingTime = endDate - buckets[buckets.length - 1].timestamp;
  const suffixMissingDays = suffixMissingTime ?
    Math.ceil(suffixMissingTime / constants.ONE_DAY) :
    0;

  // Add pre missing days
  if (prefixMissingDays) {
    for (let count = 0; count < prefixMissingDays; count += 1) {
      const timestamp = startDate + (constants.ONE_DAY * count);
      testsCountData.push({ timestamp });
      statusCountData.push({ timestamp });
    }
  }

  buckets.forEach(({ timestamp, count, aggs: { status } }) => {
    const statusData = status.reduce((acc, info) => ({
      ...acc,
      [info.name]: info.count,
    }), {});
    totalTests += count || 0;
    totalPassedTests += statusData.passed || 0;
    testsCountData.push({ timestamp, count });
    statusCountData.push({ timestamp, ...statusData });
  });

  // Add post missing days
  if (suffixMissingDays) {
    for (let count = suffixMissingDays - 1; count >= 0; count -= 1) {
      const timestamp = endDate - (constants.ONE_DAY * count);
      testsCountData.push({ timestamp });
      statusCountData.push({ timestamp });
    }
  }

  return {
    totalTests,
    totalPassedTests,
    testsCountData,
    statusCountData,
  };
};

// Paralellization efficiency percentage
// Some inaccuracy may be present because 'duration_test_max' and 'duration_absolute'
// are calculated using a formula 'session_end_time - start_time', while other fields
// using a formula 'end_time - start_time'.
// Thus, using <= instead of === and Math.max.
//
// 1 is returned if a build run in parallel, i.e. its duration is equal to the duration
// if the longest test. In other words, when duration is closer to duration_absolute,
// then efficiency is closer to 0.
export const buildEfficiency = (build) => (
  (build.duration_absolute - build.duration_test_max) <= 0 ?
    1 :
    Math.max(1 - (
      (build.duration - build.duration_test_max) /
      (build.duration_absolute - build.duration_test_max)
    ), 0)
);

export const buildStatus = ({ aggs: { status } }) => {
  if (status.length === 1) {
    return status[0].name;
  }

  if (status.find(({ name }) => name === constants.STATUSES.error.value)) {
    return constants.STATUSES.error.value;
  }

  if (status.find(({ name }) => name === constants.STATUSES.failed.value)) {
    return constants.STATUSES.failed.value;
  }

  return constants.STATUSES.complete.value;
};

export const prepareBuildsData = (builds) => (
  !Array.isArray(builds) ? [] : (
    builds.map((buildInfo) => ({
      ...buildInfo,
      tests: List(buildInfo.tests),
      efficiency: buildEfficiency(buildInfo),
      status: buildStatus(buildInfo),
    }))
  )
);

export const calculateEfficiency = (value) => {
  if (value >= 0.90) {
    return `parallelized ${(value * 100).toFixed(0)}%`;
  } if (value < 0.1) {
    return `sequential ${(value * 100).toFixed(0)}%`;
  }

  return `semi-parallel (${(value * 100).toFixed(0)}%)`;
};
export const calulateTotalTestCount = (testData) => (
  testData.reduce((prev, curr) => prev + (curr.count || 0), 0)
);

export const calculatePassedPercentage = (statusCountData, testsCountData) => {
  const testCount = calulateTotalTestCount(testsCountData);
  const statusCount = statusCountData.reduce((prev, curr) => (
    prev + (curr.passed || 0)
  ), 0);

  return testCount === 0 ?
    0 :
    ((statusCount / testCount) * 100).toFixed(0);
};

export const formatToPercent = (tick) => `${(tick * 100).toFixed(2)}%`;

export const getChartSummary = ({ testsCountData, statusCountData }) => (
  statusCountData ?
    `${calculatePassedPercentage(statusCountData, testsCountData)}% passed` :
    `${calulateTotalTestCount(testsCountData)} tests`
);

export const getSelectedOwner = (selectedOwners, item) => {
  const isSelected = selectedOwners.has(item);

  if (constants.OWNER_RADIO_OPTIONS.includes(item.name) && !isSelected) {
    return selectedOwners
      .filter((({ name }) => !constants.OWNER_RADIO_OPTIONS.includes(name)))
      .add(item);
  }

  return isSelected ?
    selectedOwners.remove(item) :
    selectedOwners.add(item);
};

export const prepareOwnersQuery = (owners) => {
  const scope = owners.find(({ name }) => constants.OWNER_RADIO_OPTIONS.includes(name));
  const ownerTeams = owners
    .filter(({ type }) => type === constants.OWNER_FILTER_KEYS.TEAM)
    .map(({ name }) => name);
  const ownerUsers = owners
    .filter(({ type }) => type === constants.OWNER_FILTER_KEYS.USER)
    .map(({ id }) => id);

  return {
    ...(scope?.name && ({ scope: scope.name })),
    ...(ownerTeams.length && ({ [constants.OWNER_FILTER_KEYS.TEAM]: ownerTeams })),
    ...(ownerUsers.length && ({ [constants.OWNER_FILTER_KEYS.USER]: ownerUsers })),
  };
};

/**
 * prepare owners queryParams for insights-v2
 *
 * @param {Array} owners;
 * @returns {Object}
 *
 * For example:
 * owners: [{ name: 'myself , id: 1}, { name: 'subacc , id: 5, type: 'owner_id_indv' }]
 * output: { 'user_id': [1, 5] }
 *
 */
export const prepareOwnersQueryV2 = (owners) => (
  owners.reduce((acc, { name, id, type }) => {
    if (
      type === constants.OWNER_FILTER_KEYS.USER ||
      name === constants.OWNER_FILTER_SCOPES.MYSELF
    ) {
      acc[constants.OWNER_FILTER_KEYS_V2.USER] = acc[constants.OWNER_FILTER_KEYS_V2.USER] || [];
      acc[constants.OWNER_FILTER_KEYS_V2.USER].push(id);
    } else if (
      type === constants.OWNER_FILTER_KEYS.TEAM ||
      name === constants.OWNER_FILTER_SCOPES.TEAM
    ) {
      acc[constants.OWNER_FILTER_KEYS_V2.TEAM] = acc[constants.OWNER_FILTER_KEYS_V2.TEAM] || [];
      acc[constants.OWNER_FILTER_KEYS_V2.TEAM].push(id);
    }
    return acc;
  }, {})
);

export const formatActivityData = (data) => (
  data.size > 1 ? Array.from(data.entries()).reduce((acc, [user, activity]) => {
    activity.forEach((entry) => {
      const index = acc.findIndex(({ datestamp2 }) => (
        datestamp2 === entry.datestamp2
      ));
      if (index < 0) {
        acc.push({
          name: entry.name,
          datestamp2: entry.datestamp2,
          [`tests_by_day2_${user}`]: entry.tests_by_day2,
          [`peak_vm_ccy2_${user}`]: entry.peak_vm_ccy2,
        });
      } else {
        acc[index] = {
          ...acc[index],
          [`tests_by_day2_${user}`]: entry.tests_by_day2,
          [`peak_vm_ccy2_${user}`]: entry.peak_vm_ccy2,
        };
      }
    });
    return acc;
  }, []) :
    data.valueSeq().first()
);

export const formatDataKeys = ({ dataPrefix, activity, colors }) => (
  Array.from(activity.keys()).map((key, index) => ({
    name: `${dataPrefix}_${key}`,
    color: colors[index],
  }))
);

export const prepareActivityRequests = ({
  user,
  scope,
  since,
  until,
  owners,
  owner_id_indv: users = [],
  owner_team: teams = [],
  dateFilterType,
}) => {
  const orgId = user.get('organization').id;
  const userId = user.get('user_id');
  const teamId = user.getUserTeamId();
  const isOrgAdmin = user.isOrgAdmin();
  const defaultParams = {
    since,
    until,
    orgId,
    dateFilterType,
  };

  const activityRequestsData = [];
  if (scope) {
    if (scope === OWNER_FILTER_SCOPES.ORGANIZATION) {
      activityRequestsData.push({
        ...defaultParams,
        isXTMAdmin: isOrgAdmin,
        username: isOrgAdmin ? 'organization' : userId,
        type: isOrgAdmin ?
          constants.ACTIVITY_REQUEST_TYPES.SCOPE_ORG_XTM :
          constants.ACTIVITY_REQUEST_TYPES.SCOPE_ORG_USER,
      });
    }

    if (scope === OWNER_FILTER_SCOPES.MYSELF) {
      activityRequestsData.push({
        ...defaultParams,
        username: userId,
        type: constants.ACTIVITY_REQUEST_TYPES.SCOPE_MYSELF,
      });
    }

    if (scope === OWNER_FILTER_SCOPES.TEAM) {
      activityRequestsData.push({
        ...defaultParams,
        teamId,
        username: user.getUserTeamName(),
        type: constants.ACTIVITY_REQUEST_TYPES.SCOPE_MYTEAM,
      });
    }
  }

  users.forEach((selectedUserName) => {
    activityRequestsData.push({
      ...defaultParams,
      username: selectedUserName,
      type: constants.ACTIVITY_REQUEST_TYPES.OWNERS_USER,
    });
  });

  teams.forEach((team) => {
    const teamInfo = owners.findEntry(({ type, name }) => (
      type === constants.OWNER_FILTER_KEYS.TEAM && name === team),
    );

    if (teamInfo?.length) {
      activityRequestsData.push({
        ...defaultParams,
        teamId: team,
        username: teamInfo[0].label,
        type: constants.ACTIVITY_REQUEST_TYPES.OWNERS_TEAM,
      });
    }
  });

  return activityRequestsData;
};

/**
 * Method used to double selected time period for API calls
 * used to compare current user data over a selected numver of days
 * with the prior same numer of days prior
 * @param {Object[]} dates
 * @param {Date} dates[].since - begining date
 * @param {Date} dates[].until - end date
 * @param {boolean} includeSince - if should include since day in difference
 * @returns {number} - time in MS
 */
export const xDaysAgo = ({ since, until }, includeSince = true) => {
  const sinceDate = fromUnixTime(since);
  const untilDate = fromUnixTime(until);

  const doubledDiff = differenceInCalendarDays(untilDate, sinceDate) * 2;

  return getUnixTime(
    subDays(
      untilDate,
      doubledDiff + (includeSince ? 1 : 0),
    ),
  );
};

export const parseJSON = (stringifiedJSON) => {
  let parsedJSON = {};
  try {
    parsedJSON = JSON.parse(stringifiedJSON);
  } catch (error) {
    parsedJSON = {};
  }
  return parsedJSON;
};

/**
 * @param {Immutable.Map|Immutable.Record} metrics
 * @param {string} selectedMetric
 * @returns {number}
 */
export const calculateHealthPercentage = (metrics, selectedMetric) => {
  const totalTestCases = metrics.getIn(['totalCasesLimited', 'value']);
  const selected = metrics.getIn([selectedMetric, 'value']);
  return totalTestCases > 0 ? (selected / totalTestCases) * 100 : 0;
};

/**
 * @param {Immutable.Map|Immutable.Record} metrics
 * @param {string} valueKey
 * @returns {number}
 */
export const calculateMixedResults = (metrics, valueKey) => {
  const totalCasesLimited = metrics.getIn(['totalCasesLimited', valueKey]);
  return Object.values(constants.TEST_PAGE_HEALTH_SNAPSHOT_LEGEND).reduce((acc, { status }) => (
    acc - metrics.getIn([status, valueKey])
  ), totalCasesLimited);
};

/**
 * @param {number} start start date timestamp
 * @param {number} end end date timestamp
 * @returns {boolean}
 */
export const isDateRangeValid = (start, end) => {
  if ((!start || !end) || (start > end)) {
    return false;
  }

  const now = set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  const limit = getTime(subDays(now, MAX_DAYS));

  if (start < limit) {
    return false;
  }

  return true;
};

export const getStorageKey = (pathname) => {
  const storageKey = constants.STORAGE_ROUTE_MAP[pathname];
  return storageKey ? `${storageKey}${FILTERS_STORE_SUFFIX}` : null;
};

export const storeFilters = (filters, pathname) => {
  const key = getStorageKey(pathname);
  const stringifyFilters = filters ? stringify(filters) : null;
  if (key) {
    window.sessionStorage.setItem(key, stringifyFilters);
  }
};

export const clearStoredFilters = (pathname) => {
  const key = getStorageKey(pathname);

  if (key) {
    window.sessionStorage.removeItem(key);
  }
};

export const getValidLocalFilters = (pathname, defaultFilters = {}) => {
  const key = getStorageKey(pathname);
  if (!key) {
    return defaultFilters;
  }
  const filters = window.sessionStorage.getItem(getStorageKey(pathname));
  const parsedFilters = filters ? parse(filters) : {};
  return getFiltersFromQueryParams(parsedFilters, defaultFilters);
};

/**
 * Get testName suffix if testName length > MAX_TEST_NAME_LENGTH (50)
 * for screenSize < MAX_WINDOW_SIZE_FOR_TEST_NAME (1366px)
 * @param {string} name
 * @param {number} windowWidth
 * @returns {string}
 */
export const getTestNameSuffix = (name, windowWidth) => {
  if (windowWidth > constants.MAX_WINDOW_SIZE_FOR_TEST_NAME) {
    return null;
  }
  const suffixStartIndex = name.length > constants.MAX_TEST_NAME_LENGTH ?
    Math.min(
      constants.DEFAULT_TEST_NAME_SUFFIX_START,
      name.length - constants.MAX_TEST_NAME_LENGTH,
    ) : 0;
  return suffixStartIndex ? name.slice(-suffixStartIndex) : null;
};

/**
 * Get sort option key by combining sort field and duration
 * @param {object} option
 * @returns {string}
 */
export const getSortOptionKey = (option) => (
  `${option.field}_${option.direction}`
);

export const TEST_CASE_PAGE_SORT_OPTIONS = [
  {
    direction: SORT_DIRECTION.DESCENDING,
    field: 'creation_time',
    label: 'Newest first',
  },
  {
    direction: SORT_DIRECTION.ASCENDING,
    field: 'creation_time',
    label: 'Oldest first',
  },
  {
    direction: SORT_DIRECTION.DESCENDING,
    field: 'duration',
    label: 'Longest job runs',
  },
  {
    direction: SORT_DIRECTION.ASCENDING,
    field: 'duration',
    label: 'Shortest job runs',
  },
].reduce((output, option) => ({
  ...output,
  [getSortOptionKey(option)]: option,
}), {});

export const getCommonStatsConfig = (stats) => (
  [
    {
      label: 'Total errors',
      value: stats.get('statuses').error,
    },
    {
      label: 'Total failures',
      value: stats.get('statuses').failed,
    },
  ]
);

export const getTestCaseStatsConfig = (stats) => ([
  {
    label: 'Total jobs run',
    value: stats.get('total'),
  },
  ...getCommonStatsConfig(stats),
  {
    label: 'Maximum job run duration',
    value: stats.get('maxDuration'),
    format: 'time',
  },
]);

export const getTestListStatsConfig = (stats) => ([
  {
    label: 'Total jobs unique names',
    info: 'Aggregated groups of job runs sharing the same name.',
    value: stats.get('total'),
  },
  ...getCommonStatsConfig(stats),
  {
    label: 'Average run time',
    value: stats.get('avgRuntime'),
    format: 'time',
  },
]);

/**
 * Get a link to single Test Case page with correct query params
 * @param {object} params required params to generate url
 * @returns {string} url string with query params
 */
export const getTestLink = ({ testRecord, filtersQuery, url }) => {
  const query = stringify({
    ...filtersQuery,
    name: testRecord.name || constants.TEST_EMPTY_NAME_DEFAULT,
  }, { arrayFormat: 'repeat' });

  return `${url}/${constants.TEST_CASE_PAGE_PATHNAME}?${query}`;
};

/**
 * Return ordered set with selected items for single select variant of filter
 * Remove currently selected item on unselect
 * Remove previously selected items and adds new item on new item select
 * @param {OrderedSet} selectedItems list of selected items
 * @param {any} item currently selected item
 * @returns {OrderedSet} new list of selected items
 */
export const getSingleSelectItem = (selectedItems, item) => (
  selectedItems.has(item) ?
    selectedItems.remove(item) :
    OrderedSet([item]));

/**
 * Check whether item matches provided search text
 * @param {string} searchText
 * @returns {(item: string | object) => boolean}
 */
export const isMatchingSearchText = (searchText = '') => (item) => {
  const searchReg = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
  const text = typeof item === 'object' && item.label ? item.label : item;
  return searchReg.test(text);
};

export const xAxisRange = ({ startDate, endDate, time }) => {
  // do not display hours if time range is longer than 48 hours
  if (differenceInDays(endDate, startDate) > CHART_BREAKPOINT_DAYS) {
    return format(time, DATE_ONLY_FORMAT);
  } // display date and time if start date or end date or new date

  return format(time, TIME_AND_DATE_FORMAT);
};
